import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ChevronRight, ArrowRight, CheckCircle2, Plug } from 'lucide-react';
import SEO from '../components/SEO';
import JsonLd, { breadcrumbSchema } from '../components/JsonLd';
import FAQSection from '../components/FAQSection';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { getSolutionBySlug } from '../data/solutionsData';

const SITE_URL = 'https://imprint-mis.co.uk';

const SolutionPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const data = getSolutionBySlug(slug);

  const [introRef, introVisible] = useScrollReveal();
  const [featRef, featVisible] = useScrollReveal();
  const [benRef, benVisible] = useScrollReveal();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (!data) navigate('/print-sectors', { replace: true });
  }, [data, navigate]);

  if (!data) return null;

  const getIcon = (name) => {
    const Icon = LucideIcons[name];
    return Icon ? <Icon size={26} /> : null;
  };

  const pageUrl = `${SITE_URL}/solutions/${data.slug}`;

  return (
    <div className="pt-0 lg:pt-28" data-testid={`solution-page-${data.slug}`}>
      <SEO title={data.seoTitle} description={data.metaDescription} url={pageUrl} />
      <JsonLd data={breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Print Sectors', path: '/print-sectors' },
        { name: data.seoTitle, path: `/solutions/${data.slug}` },
      ])} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0a0b10] py-24 lg:py-32">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-80 h-80 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #00BCE4 0%, transparent 70%)', animation: 'pulse-glow 6s ease-in-out infinite' }} />
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: 'linear-gradient(rgba(0,188,228,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,188,228,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="relative z-10 section-container">
          <nav className="flex items-center gap-2 text-sm text-white/50 mb-6 flex-wrap">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={14} />
            <Link to="/print-sectors" className="hover:text-white transition-colors">Print Sectors</Link>
            <ChevronRight size={14} />
            <span className="text-[#00BCE4]">{data.eyebrow}</span>
          </nav>
          <p className="text-[10px] font-semibold uppercase tracking-[3px] text-[#00BCE4] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            {data.eyebrow}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="solution-page-heading">
            {data.h1} <span className="text-[#00BCE4]">{data.h1Highlight}</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl leading-relaxed mb-8"
            style={{ fontFamily: 'Inter, sans-serif' }}>
            {data.heroIntro}
          </p>
          <Link to="/#contact" data-testid="solution-hero-cta" className="btn-primary text-xs">
            Book a Demo
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 lg:py-28 bg-white">
        <div ref={introRef} className={`section-container reveal ${introVisible ? 'visible' : ''}`}>
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}>
                {data.overviewTitle.replace(data.overviewHighlight, '').trim()}{' '}
                <span className="text-[#00BCE4]">{data.overviewHighlight}</span>
              </h2>
              {data.overview.map((para) => (
                <p key={para.slice(0, 40)} className="text-base text-gray-600 leading-relaxed mb-5"
                  style={{ fontFamily: 'Inter, sans-serif' }}>
                  {para}
                </p>
              ))}
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-100">
              <img src={data.heroImage} alt={`${data.h1} ${data.h1Highlight}`} loading="lazy"
                className="w-full h-72 lg:h-96 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div ref={featRef} className={`section-container reveal ${featVisible ? 'visible' : ''}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            What&apos;s <span className="text-[#00BCE4]">included</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {data.features.map((f) => (
              <div key={f.title} data-testid={`solution-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="bg-white border border-gray-200 rounded-2xl p-7 hover:border-[#00BCE4]/40 hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 rounded-xl bg-[#00BCE4]/10 flex items-center justify-center text-[#00BCE4] mb-5">
                  {getIcon(f.icon)}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {f.title}
                </h3>
                <p className="text-base text-gray-600 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits + Integrations */}
      <section className="py-20 lg:py-28 bg-white">
        <div ref={benRef} className={`section-container reveal ${benVisible ? 'visible' : ''}`}>
          <div className="grid lg:grid-cols-2 gap-14">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8"
                style={{ fontFamily: 'Outfit, sans-serif' }}>
                Why printers <span className="text-[#00BCE4]">choose it</span>
              </h2>
              <ul className="space-y-4">
                {data.benefits.map((b) => (
                  <li key={b.slice(0, 40)} className="flex items-start gap-3">
                    <CheckCircle2 size={22} className="text-[#00BCE4] flex-shrink-0 mt-0.5" />
                    <span className="text-base text-gray-600 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {b}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#0a0b10] rounded-2xl p-8 lg:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10"
                style={{ background: 'radial-gradient(circle, #00BCE4 0%, transparent 70%)' }} />
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-xl bg-[#00BCE4]/20 flex items-center justify-center text-[#00BCE4] mb-5">
                  <Plug size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Works with your stack
                </h3>
                <p className="text-base text-gray-300 leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {data.integrationsNote}
                </p>
                <Link to="/integrations" className="inline-flex items-center gap-2 text-sm font-semibold text-[#00BCE4] hover:text-white transition-colors">
                  See all integrations <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQSection
        id="solution-faq"
        eyebrow="FAQs"
        title={`${data.h1} ${data.h1Highlight} —`}
        highlight="answered"
        faqs={data.faqs}
      />

      {/* Related solutions (internal linking for SEO) */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="section-container">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Related <span className="text-[#00BCE4]">solutions</span>
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {data.related.map((r) => (
              <Link key={r.to} to={r.to}
                data-testid={`solution-related-${r.to.split('/').pop()}`}
                className="group flex items-center justify-between bg-white border border-gray-200 rounded-xl px-5 py-4 hover:border-[#00BCE4] hover:shadow-md transition-all duration-300">
                <span className="text-sm font-semibold text-gray-800 group-hover:text-[#00BCE4] transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {r.label}
                </span>
                <ArrowRight size={16} className="text-gray-300 group-hover:text-[#00BCE4] transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#00BCE4] py-16 lg:py-24">
        <div className="section-container text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            See {data.h1} {data.h1Highlight} in action
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8 text-base md:text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
            Book a no-obligation demo and we&apos;ll show you exactly how Imprint MIS fits your business.
          </p>
          <Link to="/#contact" data-testid="solution-final-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#0a0b10] font-bold text-sm rounded-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            Request a Demo <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SolutionPage;
